import { useQuery } from "@tanstack/react-query";
import { facilityApi } from "@bw/shared/api";
import type { BookingParticipantResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The participant roster for one booking (GET /facilities/{facilityId}/bookings/
 * {bookingId}/participants), backing the booking-detail roster.
 *
 * Returns the server's BookingParticipantResponse[] VERBATIM — the page renders
 * each row's resolved display_name (em-dash for a null name, NEVER the raw uid),
 * its host/guest role badge, and the checked_in_at indicator, IN SERVER ORDER
 * (no client re-sort). Keyed by (facilityId, bookingId) so each booking's roster
 * is isolated. An add/remove mutation invalidates this key so the list re-reads
 * the server's truth — never an optimistic fabricated row.
 */
export function useBookingParticipants(
  facilityId: string | undefined,
  bookingId: string | undefined,
) {
  return useQuery<BookingParticipantResponse[]>({
    queryKey: queryKeys.booking.participants(facilityId ?? "", bookingId ?? ""),
    queryFn: () =>
      facilityApi.listBookingParticipants(
        facilityId as string,
        bookingId as string,
      ),
    enabled: Boolean(facilityId) && Boolean(bookingId),
  });
}
